import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Reveal from "@/components/sites/Reveal";
import EstatePropertyCard from "@/components/sites/EstatePropertyCard";
import PreviewBadge from "@/components/sites/PreviewBadge";
import { EstateHeader, EstateFooter, EstateStickyCta } from "@/components/sites/EstateChrome";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api } from "@/lib/api";
import { t } from "@/lib/terms";
import { withPreview } from "@/lib/sitePreview";

/**
 * The search page, in the Estate theme.
 *
 * The design draws six filters (location, min price, max price, bedrooms,
 * type, and a for-sale/to-let/map switch) with hard-coded price bands. The
 * public listings endpoint takes postcode, listing_type, min_bedrooms and
 * max_price, so those four are the ones here: a filter that cannot narrow
 * anything is a control that lies about what it does.
 *
 * Price bands are derived from the agency's own listings rather than the
 * design's £300k/£600k/£1m, which are Surrey numbers. An agency in Hull
 * would have every home in one band.
 */

/** Round bands that actually split this agency's listings. */
export function priceBands(prices) {
  const sorted = prices.filter((n) => n > 0).sort((a, b) => a - b);
  if (sorted.length < 3) return [];
  const step = (n) => {
    const mag = n >= 1_000_000 ? 250_000 : n >= 400_000 ? 50_000 : 25_000;
    return Math.round(n / mag) * mag;
  };
  const at = (f) => step(sorted[Math.floor((sorted.length - 1) * f)]);
  return [...new Set([at(0.25), at(0.5), at(0.75), at(1)])].filter((n) => n > 0);
}

const money = (n) => (n >= 1_000_000
  ? `£${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}m`
  : `£${Math.round(n / 1000)}k`);

export default function EstateListings({ preview = true }) {
  const brand = useBrand();
  const market = useMarket();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [allPrices, setAllPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const p = (href) => withPreview(href, preview);

  const listingType = params.get("listing_type") === "letting" ? "letting" : "sale";
  const postcode = params.get("postcode") || "";
  const minBeds = params.get("min_bedrooms") || "";
  const maxPrice = params.get("max_price") || "";

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (preview) next.set("theme", "estate");
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!tenantKnown) return;
    setLoading(true);
    try {
      const q = { tenant: tenantSlug, listing_type: listingType, limit: 60 };
      if (minBeds) q.min_bedrooms = Number(minBeds);
      if (maxPrice) q.max_price = Number(maxPrice);
      if (postcode) q.postcode = postcode;
      const { data } = await api.get("/crm/public/properties", { params: q });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, tenantKnown, listingType, minBeds, maxPrice, postcode]);

  useEffect(() => { load(); }, [load]);

  // The unfiltered set, once, purely to size the price bands. Asking the
  // filtered endpoint would shrink the bands as somebody narrows, which
  // makes the control move under their hand.
  useEffect(() => {
    if (!tenantKnown) return undefined;
    let stale = false;
    api.get(`/crm/public/properties?tenant=${tenantSlug}&listing_type=${listingType}&limit=60`)
      .then(({ data }) => {
        if (stale) return;
        setAllPrices((data.items || []).map((i) => (listingType === "letting" ? i.rental_pcm : i.asking_price) || 0));
      })
      .catch(() => {});
    return () => { stale = true; };
  }, [tenantSlug, tenantKnown, listingType]);

  const bands = useMemo(() => priceBands(allPrices), [allPrices]);
  const areas = brand.areas_served || [];
  const chips = [
    postcode ? { k: "postcode", label: postcode } : null,
    minBeds ? { k: "min_bedrooms", label: `${minBeds}+ bed` } : null,
    maxPrice ? { k: "max_price", label: `Up to ${money(Number(maxPrice))}` } : null,
  ].filter(Boolean);

  const heading = listingType === "letting" ? t("to_let", market) : "For sale";
  const where = areas.length ? areas[0] : "";

  return (
    <div className="fs" data-testid="estate-listings">
      <SeoHead
        title={`Property ${heading.toLowerCase()}${where ? ` in ${where}` : ""} | ${biz.name}`}
        description={brand.business?.elevator_pitch}
      />
      <a className="skip" href="#main">Skip to content</a>
      <EstateHeader preview={preview} />

      {/* A viewport of reserved height until the agency's own details are
          known, because the heading is built from them: "Property for
          sale" becomes "Property for sale in Windlesham and the
          surrounding area" the moment /brand answers, which is two or
          three more wrapped lines on a phone. Painting the short one
          first and correcting it pushed the lede, the filters and the
          footer down 188px in one go: a measured CLS of 0.27 to 0.47,
          where anything above 0.1 is a failing Core Web Vital.

          So nothing paints until /brand answers. The header is already
          there, so this is not a blank screen; it is a screen of the
          agency's own chrome over a reserved space, which then fills in
          one paint at its final geometry.

          The screen stays reserved afterwards rather than only until the
          brand lands, because the listings are a SECOND request: for the
          moment between them the page is a heading and an empty grid,
          which is shorter than a phone, and letting it collapse to that
          brought the footer up into view to be shoved down again when
          the results arrived. `min-h-screen` on the classic pages is the
          same line for the same reason. */}
      <main id="main" style={{ minHeight: "100vh" }}>
        {brand.brand_resolved && (<>
          <div className="container page-head">
            <ol className="crumbs">
              <li><Link to={p(tenantPath(""))}>Home</Link></li>
              <li>Property {heading.toLowerCase()}</li>
            </ol>
            <h1 className="display-l">
              Property {heading.toLowerCase()}
              {where ? <> in <span className="serif-i">{where}</span> and the surrounding area</> : null}
            </h1>
            {brand.regions_covered_prose && (
              <p className="lede" style={{ marginTop: 16 }}>
                Everything we are instructed on across {brand.regions_covered_prose}.
              </p>
            )}
          </div>

          <div className="container">
            <form className="filters" onSubmit={(e) => { e.preventDefault(); load(); }}>
              <div className="field">
                <label htmlFor="fs-where">Location</label>
                <input id="fs-where" value={postcode} placeholder={areas.slice(0, 2).join(", ") || "Town or postcode"}
                       onChange={(e) => setParam("postcode", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="fs-beds">Bedrooms</label>
                <select id="fs-beds" value={minBeds} onChange={(e) => setParam("min_bedrooms", e.target.value)}>
                  <option value="">Any</option>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="fs-max">Max price</label>
                <select id="fs-max" value={maxPrice} onChange={(e) => setParam("max_price", e.target.value)}>
                  <option value="">No max</option>
                  {bands.map((n) => <option key={n} value={n}>{money(n)}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="fs-type">Showing</label>
                <select id="fs-type" value={listingType}
                        onChange={(e) => setParam("listing_type", e.target.value === "letting" ? "letting" : "")}>
                  <option value="sale">For sale</option>
                  <option value="letting">{t("to_let", market)}</option>
                </select>
              </div>
              <button className="btn" type="submit">Search</button>
            </form>

            <div className="results-head">
              <div className="count num">
                {loading ? "Searching…" : `${items.length} ${items.length === 1 ? "home" : "homes"}`}
              </div>
              {chips.length > 0 && (
                <div className="chips">
                  {chips.map((c) => (
                    <button type="button" className="chip" key={c.k} onClick={() => setParam(c.k, "")}>
                      {c.label} &times;
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!loading && items.length === 0 ? (
              <div className="empty" data-testid="estate-no-results">
                <h3>Nothing matching that right now</h3>
                <p>
                  {chips.length > 0
                    ? "Try widening the search, or register and we will tell you the moment something comes to us."
                    : `We have nothing ${heading.toLowerCase()} at the moment. Register and we will tell you the moment something comes to us.`}
                </p>
                <Link className="btn" to={p(tenantPath("register"))}>
                  Register as a buyer <span className="arrow" aria-hidden="true">&rarr;</span>
                </Link>
              </div>
            ) : (
              <div className="grid cols-3" style={{ paddingBottom: "clamp(56px, 9vw, 112px)" }}>
                {items.map((l) => (
                  <Reveal key={l.id}>
                    <EstatePropertyCard property={l} market={market}
                                        href={p(`${tenantPath("properties")}/${l.website_slug || l.id}`)} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </>)}
      </main>

      <EstateFooter preview={preview} />
      <EstateStickyCta preview={preview} />
      {preview && <PreviewBadge />}
    </div>
  );
}
